import React from 'react';
import { Stepper, Group, Text, Badge, Paper, ActionIcon } from '@mantine/core';
import {
  IconCheck,
  IconFileText,
  IconFlask,
  IconClipboardCheck,
  IconUpload,
  IconUserCheck,
  IconSend,
  IconBell,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { OrderSubState } from '../lims-types';
import { ServiceRequest } from '@medplum/fhirtypes';
import {
  getAllOrderSubStates,
  getOrderSubState,
  getSubStateStepIndex,
  getSubStateLabel,
} from '../utils/order-substates-helper';

// -----------------------------------------------------------------------------
interface OrderSubStateStepperProps {
  serviceRequest: ServiceRequest;
  onSubStateChange?: (newSubState: OrderSubState) => Promise<void>;
  isEditable?: boolean;
}

// -----------------------------------------------------------------------------
const subStateIcons: Record<OrderSubState, React.ReactNode> = {
  [OrderSubState.OrderCreated]: <IconFileText size={14} />,
  [OrderSubState.SampleReceived]: <IconFlask size={14} />,
  [OrderSubState.ConsentComplete]: <IconClipboardCheck size={14} />,
  [OrderSubState.ResultsUploaded]: <IconUpload size={14} />,
  [OrderSubState.DirectorApproved]: <IconUserCheck size={14} />,
  [OrderSubState.ResultsReleased]: <IconSend size={14} />,
  [OrderSubState.PatientNotified]: <IconBell size={14} />,
};

// -----------------------------------------------------------------------------
export const OrderSubStateStepper = ({
  serviceRequest,
  onSubStateChange,
  isEditable = false,
}: OrderSubStateStepperProps) => {
  const { t } = useTranslation('lims');
  const currentSubState = getOrderSubState(serviceRequest);
  const allSubStates = getAllOrderSubStates();
  const currentStepIndex = getSubStateStepIndex(currentSubState);

  // ---------------------------------------------------------------------------
  const handleStepChange = async (newStepIndex: number) => {
    if (!isEditable || !onSubStateChange) return;

    const newSubState = allSubStates[newStepIndex];
    if (newSubState && newSubState !== currentSubState) {
      await onSubStateChange(newSubState);
    }
  };

  // ---------------------------------------------------------------------------
  const canGoBack = isEditable && currentStepIndex > 0;
  const canGoForward = isEditable && currentStepIndex < allSubStates.length - 1;

  // ---------------------------------------------------------------------------
  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" mb="md">
        <Text size="sm" fw={600}>
          {t('orderSubStateStepper.title')}
        </Text>
        <Group>
          {currentSubState && (
            <Badge color="blue" variant="light">
              {t(getSubStateLabel(currentSubState))}
            </Badge>
          )}
          {isEditable && (
            <Group gap="xs">
              <ActionIcon
                variant="outline"
                size="sm"
                disabled={!canGoBack}
                onClick={() => handleStepChange(currentStepIndex - 1)}
              >
                <IconChevronLeft size={16} />
              </ActionIcon>
              <ActionIcon
                variant="outline"
                size="sm"
                disabled={!canGoForward}
                onClick={() => handleStepChange(currentStepIndex + 1)}
              >
                <IconChevronRight size={16} />
              </ActionIcon>
            </Group>
          )}
        </Group>
      </Group>

      <Stepper
        active={currentStepIndex}
        orientation="horizontal"
        size="sm"
        allowNextStepsSelect={isEditable}
        onStepClick={isEditable ? handleStepChange : undefined}
        styles={{
          step: {
            minWidth: '80px',
          },
          stepLabel: {
            fontSize: '11px',
            lineHeight: '1.2',
            textAlign: 'center',
            maxWidth: '80px',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
          },
          stepDescription: {
            display: 'none', // Hide description to save space
          },
          content: {
            paddingTop: '8px',
          },
        }}
      >
        {allSubStates.map((subState, index) => (
          <Stepper.Step
            key={subState}
            label={t(getSubStateLabel(subState))}
            icon={
              index <= currentStepIndex ? (
                <IconCheck size={14} />
              ) : (
                subStateIcons[subState]
              )
            }
            completedIcon={<IconCheck size={14} />}
            color={index <= currentStepIndex ? 'green' : 'gray'}
          />
        ))}
      </Stepper>
    </Paper>
  );
};
